import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, LogOut, Trash2, Eye, Star, BarChart3, Mail, Plus, X,
  Upload, ImageIcon, ChevronUp, ChevronDown, Save, Edit3, FileImage
} from "lucide-react";
import { API_URL, BRAND, resolveAsset } from "../lib/brand";

const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

const resolveUrl = resolveAsset;

function emptyForm() {
  const now = new Date();
  return {
    id: null,
    slug: "",
    number: "",
    title: "Crescere con Cura",
    subtitle: "",
    month_label: "",
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    cover_image: "",
    tagline: "",
    description: "",
    pages: [],
    is_featured: false,
    published: true,
    publish_at: "",  // local-datetime string, e.g. "2026-09-01T10:00"
  };
}

function slugify(text) {
  return (text || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// Convert ISO datetime (from API) into a value acceptable for <input type="datetime-local">
// Keeps the same "wall clock" the user sees in their browser locale.
function isoToLocal(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

export default function AdminMagazines() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingPages, setUploadingPages] = useState(false);
  const coverInputRef = useRef(null);
  const pagesInputRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("tv_admin_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/admin/magazines`, auth);
      setItems(r.data);
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem("tv_admin_token"); navigate("/admin/login"); }
    } finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo numero del magazine?")) return;
    try {
      await axios.delete(`${API_URL}/admin/magazines/${id}`, auth);
      toast.success("Numero eliminato");
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const togglePublished = async (m) => {
    try {
      const payload = { ...m, published: !m.published };
      delete payload.id;
      delete payload.published_at;
      delete payload.created_at;
      await axios.put(`${API_URL}/admin/magazines/${m.id}`, payload, auth);
      toast.success(!m.published ? "Pubblicato" : "Nascosto");
      load();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const toggleFeatured = async (m) => {
    try {
      const payload = { ...m, is_featured: !m.is_featured };
      delete payload.id;
      delete payload.published_at;
      delete payload.created_at;
      await axios.put(`${API_URL}/admin/magazines/${m.id}`, payload, auth);
      toast.success(!m.is_featured ? "Impostato come In evidenza" : "Rimosso da In evidenza");
      load();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const openNew = () => {
    setForm(emptyForm());
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openEdit = (m) => {
    setForm({
      id: m.id,
      slug: m.slug || "",
      number: m.number || "",
      title: m.title || "Crescere con Cura",
      subtitle: m.subtitle || "",
      month_label: m.month_label || "",
      year: m.year || new Date().getFullYear(),
      month: m.month || 1,
      cover_image: m.cover_image || "",
      tagline: m.tagline || "",
      description: m.description || "",
      pages: [...(m.pages || [])],
      is_featured: !!m.is_featured,
      published: m.published !== false,
      publish_at: m.publish_at ? isoToLocal(m.publish_at) : "",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelForm = () => {
    setShowForm(false);
    setForm(emptyForm());
  };

  // Auto-populate month_label + slug + number when month/year/number change
  const updateField = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      if (patch.month != null || patch.year != null) {
        const m = MONTHS_IT[(next.month || 1) - 1];
        next.month_label = `${m} ${next.year}`;
      }
      if ((patch.number != null || patch.month != null || patch.year != null) && !prev.id) {
        const numSlug = (next.number || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
        const m = MONTHS_IT[(next.month || 1) - 1] || "";
        next.slug = slugify(`${numSlug}-${m}-${next.year}`);
      }
      return next;
    });
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "magazine");
    const r = await axios.post(`${API_URL}/admin/upload`, fd, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
    });
    return r.data.url;
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const url = await uploadFile(file);
      updateField({ cover_image: url });
      toast.success("Copertina caricata");
    } catch (err) {
      toast.error("Errore caricamento copertina");
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const handlePagesUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPages(true);
    try {
      const urls = [];
      for (const f of files) {
        const u = await uploadFile(f);
        urls.push(u);
      }
      setForm((prev) => ({ ...prev, pages: [...prev.pages, ...urls] }));
      toast.success(`${urls.length} pagina/e caricata/e`);
    } catch (err) {
      toast.error("Errore caricamento pagine");
    } finally {
      setUploadingPages(false);
      if (pagesInputRef.current) pagesInputRef.current.value = "";
    }
  };

  const movePage = (idx, dir) => {
    setForm((prev) => {
      const arr = [...prev.pages];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...prev, pages: arr };
    });
  };

  const removePage = (idx) => {
    setForm((prev) => ({ ...prev, pages: prev.pages.filter((_, i) => i !== idx) }));
  };

  const validate = () => {
    if (!form.slug) return "Slug obbligatorio";
    if (!form.number) return "Numero obbligatorio (es. N.02)";
    if (!form.month_label) return "Mese/anno obbligatorio";
    if (!form.cover_image) return "Copertina obbligatoria";
    if (!form.pages.length) return "Aggiungi almeno una pagina";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        slug: form.slug,
        number: form.number,
        title: form.title || "Crescere con Cura",
        subtitle: form.subtitle || "",
        month_label: form.month_label,
        year: Number(form.year),
        month: Number(form.month),
        cover_image: form.cover_image,
        tagline: form.tagline || "",
        description: form.description || "",
        pages: form.pages,
        is_featured: !!form.is_featured,
        published: !!form.published,
        publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : null,
      };
      if (form.id) {
        await axios.put(`${API_URL}/admin/magazines/${form.id}`, payload, auth);
        toast.success("Numero aggiornato");
      } else {
        await axios.post(`${API_URL}/admin/magazines`, payload, auth);
        toast.success("Numero creato");
      }
      cancelForm();
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore salvataggio";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const logout = () => { localStorage.removeItem("tv_admin_token"); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#FAF8F5] p-6 md:p-10" data-testid="admin-magazines-page">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <img src={BRAND.logo} alt="" className="h-12 w-12" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768]">— Crescere con Cura</p>
              <h1 className="font-display text-3xl md:text-4xl text-[#3D2140]">Magazine</h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]" data-testid="link-dashboard"><ArrowLeft size={14} /> Dashboard</Link>
            <Link to="/admin/newsletter" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]"><Mail size={14} /> Newsletter</Link>
            <Link to="/admin/statistiche" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]"><BarChart3 size={14} /> Statistiche</Link>
            <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]" data-testid="btn-logout"><LogOut size={14} /> Esci</button>
          </div>
        </div>

        {/* Editor form */}
        {showForm && (
          <div className="bg-white rounded-2xl border border-[#FF2768]/20 shadow-sm p-6 mb-6" data-testid="magazine-form">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#FF2768] font-bold">{form.id ? "Modifica numero" : "Nuovo numero"}</p>
                <h2 className="font-display text-2xl text-[#3D2140]">
                  {form.id ? form.month_label || "Numero" : "Aggiungi un nuovo numero"}
                </h2>
              </div>
              <button onClick={cancelForm} className="p-2 rounded-full hover:bg-[#FAF8F5]" data-testid="btn-close-form"><X size={18} /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* LEFT: metadata */}
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Numero *</label>
                    <input value={form.number} onChange={(e) => updateField({ number: e.target.value })} placeholder="N.02" className="w-full px-3 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768]" data-testid="input-number" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Mese *</label>
                    <select value={form.month} onChange={(e) => updateField({ month: Number(e.target.value) })} className="w-full px-3 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768]" data-testid="input-month">
                      {MONTHS_IT.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Anno *</label>
                    <input type="number" value={form.year} onChange={(e) => updateField({ year: Number(e.target.value) })} className="w-full px-3 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768]" data-testid="input-year" />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Slug URL *</label>
                  <input value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} placeholder="n02-ottobre-2026" className="w-full px-3 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768]" data-testid="input-slug" />
                  <p className="text-[10px] text-[#3D2140]/50 mt-1">Sarà l'URL: /magazine/{form.slug || "..."}</p>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Titolo copertina</label>
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Crescere con Cura" className="w-full px-3 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768]" data-testid="input-title" />
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Sottotitolo (copertina)</label>
                  <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="Il magazine di Teresa Vilardo" className="w-full px-3 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768]" data-testid="input-subtitle" />
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Tagline</label>
                  <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Sguardi, pensieri, piccoli strumenti..." className="w-full px-3 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768]" data-testid="input-tagline" />
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Descrizione (per la scheda pubblica)</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} placeholder="Un breve editoriale del numero..." className="w-full px-3 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768] resize-none" data-testid="input-description" />
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Programma pubblicazione (opzionale)</label>
                  <input
                    type="datetime-local"
                    value={form.publish_at}
                    onChange={(e) => setForm({ ...form, publish_at: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm outline-none focus:border-[#FF2768]"
                    data-testid="input-publish-at"
                  />
                  <p className="text-[10px] text-[#3D2140]/50 mt-1">
                    Se compilato, il numero sarà visibile online solo a partire da questa data e ora. Lascia vuoto per pubblicare subito.
                  </p>
                </div>

                <div className="flex flex-wrap gap-4">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-[#FF2768]" data-testid="input-published" />
                    <span className="text-sm text-[#3D2140]">Pubblicato (visibile online)</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} className="w-4 h-4 accent-[#FFE45C]" data-testid="input-featured" />
                    <span className="text-sm text-[#3D2140]">In evidenza (numero principale)</span>
                  </label>
                </div>
              </div>

              {/* RIGHT: cover + pages upload */}
              <div className="space-y-4">
                {/* Cover */}
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Copertina *</label>
                  <div className="flex items-start gap-3">
                    <div className="w-28 h-40 rounded-lg overflow-hidden bg-[#FAF8F5] border border-[#3D2140]/10 flex items-center justify-center flex-shrink-0">
                      {form.cover_image
                        ? <img src={resolveUrl(form.cover_image)} alt="Copertina" className="w-full h-full object-cover" data-testid="preview-cover" />
                        : <ImageIcon size={24} className="text-[#3D2140]/30" />}
                    </div>
                    <div className="flex-1">
                      <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" data-testid="input-cover-file" />
                      <button onClick={() => coverInputRef.current?.click()} disabled={uploadingCover} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#FF2768] text-white text-sm font-semibold hover:bg-[#e91e5e] disabled:opacity-50" data-testid="btn-upload-cover">
                        <Upload size={14} /> {uploadingCover ? "Sto caricando..." : (form.cover_image ? "Sostituisci copertina" : "Carica copertina")}
                      </button>
                      <p className="text-[10px] text-[#3D2140]/50 mt-1.5">PNG o JPG, formato verticale consigliato (es. 900x1200px)</p>
                    </div>
                  </div>
                </div>

                {/* Pages */}
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-semibold mb-1.5">Pagine ({form.pages.length}) *</label>
                  <input ref={pagesInputRef} type="file" accept="image/*" multiple onChange={handlePagesUpload} className="hidden" data-testid="input-pages-file" />
                  <button onClick={() => pagesInputRef.current?.click()} disabled={uploadingPages} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#81C3C9] text-white text-sm font-semibold hover:bg-[#6bacb2] disabled:opacity-50 mb-3" data-testid="btn-upload-pages">
                    <FileImage size={14} /> {uploadingPages ? "Sto caricando..." : "Aggiungi pagine (seleziona più file)"}
                  </button>

                  <div className="max-h-80 overflow-y-auto space-y-1.5 border border-[#3D2140]/8 rounded-xl p-2 bg-[#FAF8F5]/50">
                    {form.pages.length === 0 && (
                      <p className="text-center py-6 text-xs italic text-[#3D2140]/50">Nessuna pagina caricata. Carica in ordine di lettura.</p>
                    )}
                    {form.pages.map((url, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-white rounded-lg p-2 border border-[#3D2140]/8" data-testid={`page-row-${idx}`}>
                        <img src={resolveUrl(url)} alt="" className="w-10 h-12 object-cover rounded border border-[#3D2140]/10" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-[#3D2140] font-semibold">Pagina {idx + 1}</p>
                          <p className="text-[10px] text-[#3D2140]/50 truncate">{url.split("/").pop()}</p>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => movePage(idx, -1)} disabled={idx === 0} className="p-1.5 rounded hover:bg-[#FAF8F5] disabled:opacity-30" title="Su" data-testid={`page-up-${idx}`}><ChevronUp size={14} /></button>
                          <button onClick={() => movePage(idx, 1)} disabled={idx === form.pages.length - 1} className="p-1.5 rounded hover:bg-[#FAF8F5] disabled:opacity-30" title="Giù" data-testid={`page-down-${idx}`}><ChevronDown size={14} /></button>
                          <button onClick={() => removePage(idx)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Elimina" data-testid={`page-del-${idx}`}><Trash2 size={13} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Save bar */}
            <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-[#3D2140]/8">
              <button onClick={cancelForm} className="px-5 py-2.5 rounded-full bg-white border border-[#3D2140]/15 text-[#3D2140] text-sm font-semibold hover:border-[#FF2768]" data-testid="btn-cancel">Annulla</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#FF2768] text-white text-sm font-semibold hover:bg-[#e91e5e] disabled:opacity-50" data-testid="btn-save">
                <Save size={14} /> {saving ? "Salvataggio..." : (form.id ? "Salva modifiche" : "Crea numero")}
              </button>
            </div>
          </div>
        )}

        {/* Toolbar */}
        {!showForm && (
          <div className="bg-white rounded-2xl border border-[#3D2140]/8 p-5 mb-6 flex items-center justify-between flex-wrap gap-4">
            <p className="text-sm text-[#3D2140]/80 leading-relaxed max-w-2xl">
              Da qui gestisci i numeri di <strong>Crescere con Cura</strong>. Crea un nuovo numero caricando copertina e pagine (PNG o JPG), riordina le pagine con le frecce, poi pubblicalo quando è pronto.
            </p>
            <button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF2768] text-white text-sm font-semibold hover:bg-[#e91e5e] whitespace-nowrap" data-testid="btn-new-magazine">
              <Plus size={16} /> Nuovo numero
            </button>
          </div>
        )}

        {/* List */}
        <div className="bg-white rounded-2xl border border-[#3D2140]/8 overflow-hidden">
          {loading && <p className="p-10 text-center font-editorial italic text-[#3D2140]/60">Sto caricando...</p>}
          {!loading && items.length === 0 && (
            <div className="p-10 text-center">
              <p className="font-editorial italic text-[#3D2140]/60 mb-4">Nessun numero ancora.</p>
              {!showForm && (
                <button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF2768] text-white text-sm font-semibold hover:bg-[#e91e5e]" data-testid="btn-new-empty">
                  <Plus size={16} /> Crea il primo numero
                </button>
              )}
            </div>
          )}
          {items.map((m) => {
            const scheduled = m.publish_at && new Date(m.publish_at) > new Date();
            return (
            <div key={m.id} className="flex flex-wrap items-center gap-4 p-5 border-b border-[#3D2140]/5 last:border-0 hover:bg-[#FAF8F5]/50" data-testid={`magazine-row-${m.slug}`}>
              <img src={resolveUrl(m.cover_image)} alt={m.month_label} className="h-24 rounded-lg object-cover shadow-sm border border-[#3D2140]/10" style={{ width: 72 }} />
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {scheduled ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold text-white" style={{ background: "#F59E0B" }} data-testid={`magazine-scheduled-${m.slug}`}>
                      Programmato · {new Date(m.publish_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold text-white" style={{ background: m.published ? "#10B981" : "#9CA3AF" }}>
                      {m.published ? "Pubblicato" : "Nascosto"}
                    </span>
                  )}
                  {m.is_featured && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold bg-[#FFE45C]/60 text-[#3D2140]"><Star size={10} className="fill-current" /> In evidenza</span>}
                  <p className="text-[10px] uppercase tracking-widest text-[#FF2768] font-bold">{m.number}</p>
                </div>
                <p className="font-display text-xl text-[#3D2140]">{m.month_label}</p>
                {m.tagline && <p className="text-sm text-[#3D2140]/60 italic mt-1 line-clamp-1">"{m.tagline}"</p>}
                <p className="text-xs text-[#3D2140]/40 mt-1">
                  {m.pages?.length || 0} pagine
                  {scheduled && ` · pubblica il ${new Date(m.publish_at).toLocaleString("it-IT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}`}
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Link to={`/magazine/${m.slug}`} target="_blank" className="p-2 rounded-full bg-[#FAF8F5] hover:bg-[#81C3C9] hover:text-white" title="Anteprima pubblica" data-testid={`magazine-view-${m.slug}`}>
                  <Eye size={14} />
                </Link>
                <button onClick={() => openEdit(m)} className="p-2 rounded-full bg-[#FAF8F5] hover:bg-[#FF2768] hover:text-white" title="Modifica" data-testid={`magazine-edit-${m.slug}`}>
                  <Edit3 size={14} />
                </button>
                <button onClick={() => togglePublished(m)} className={`px-3 py-2 rounded-full text-xs font-semibold ${m.published ? "bg-[#FAF8F5] text-[#3D2140]" : "bg-[#10B981] text-white"}`} data-testid={`magazine-pub-${m.slug}`}>
                  {m.published ? "Nascondi" : "Pubblica"}
                </button>
                <button onClick={() => toggleFeatured(m)} className={`px-3 py-2 rounded-full text-xs font-semibold ${m.is_featured ? "bg-[#FFE45C]/60 text-[#3D2140]" : "bg-[#FAF8F5] text-[#3D2140]"}`} title="In evidenza" data-testid={`magazine-star-${m.slug}`}>
                  <Star size={12} className={m.is_featured ? "fill-current" : ""} />
                </button>
                <button onClick={() => del(m.id)} className="p-2 rounded-full bg-[#FAF8F5] hover:bg-red-500 hover:text-white" title="Elimina" data-testid={`magazine-del-${m.slug}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
